import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ExperimentData, Participant, ExperimentPhase, CrossDomainMetrics } from '../types';
import { FTTClassification } from '../utils/fttClassifier';

interface ExperimentContextType {
  experimentData: ExperimentData | null;
  participant: Participant | null;
  currentPhase: ExperimentPhase;
  updatePhase: (phase: ExperimentPhase) => void;
  updateParticipant: (updates: Partial<Participant>) => void;
  saveResponse: (response: any) => void;
  updateProgress: (progress: number) => void;
  // FTT data management
  fttData: {
    emailClassifications: Map<string, FTTClassification>;
    deviceClassifications: Map<string, FTTClassification>;
    crossDomainMetrics: CrossDomainMetrics | null;
  };
  updateFTTClassification: (domain: 'email' | 'device' | 'info-sharing', id: string, classification: FTTClassification) => void;
}

const ExperimentContext = createContext<ExperimentContextType | undefined>(undefined);

export const useExperiment = () => {
  const context = useContext(ExperimentContext);
  if (!context) {
    // In development, return a mock context instead of throwing
    if (process.env.NODE_ENV === 'development') {
      console.warn('useExperiment: No ExperimentProvider found, returning mock context');
      return {
        experimentData: {
          participant: {
            id: 'hook-mock-' + Date.now(),
            phase: 'instructions' as ExperimentPhase,
            demographics: { age: '25-34', gender: 'prefer-not-to-say', education: 'bachelors' },
            conditions: { studyNumber: 1, taskOrder: 'email-first', timePressure: 'low', threatSalience: 'low', sanctionVisibility: 'implicit', priorExperience: 'none', congruence: 'congruent' },
            taskAssignment: {
              cohort: 'email-coding',
              taskOrder: 'email-first',
              primaryTask: 'email',
              secondaryTask: 'coding',
              stratum: 'coder',
              reasoning: 'Hook mock assignment'
            },
            responses: []
          },
          startTime: Date.now()
        },
        participant: null,
        currentPhase: 'instructions' as ExperimentPhase,
        updatePhase: () => {},
        updateParticipant: () => {},
        saveResponse: () => {},
        updateProgress: () => {},
        fttData: {
          emailClassifications: new Map(),
          deviceClassifications: new Map(),
          crossDomainMetrics: null
        },
        updateFTTClassification: () => {}
      } as unknown as ExperimentContextType;
    }
    throw new Error('useExperiment must be used within ExperimentProvider');
  }
  return context;
};

interface ExperimentProviderProps {
  children: ReactNode;
  value: any;
}

export const ExperimentProvider: React.FC<ExperimentProviderProps> = ({ children, value }) => {
  // In development mode, provide mock data if none exists
  const getMockData = () => {
    if (process.env.NODE_ENV === 'development' && !value) {
      return {
        participant: {
          id: 'dev-mock-' + Date.now(),
          phase: 'post-experiment' as ExperimentPhase,
          demographics: {
            age: '25-34',
            gender: 'prefer-not-to-say',
            education: 'bachelors'
          },
          conditions: {
            studyNumber: 1,
            taskOrder: 'email-first',
            timePressure: 'low',
            threatSalience: 'low',
            sanctionVisibility: 'implicit',
            priorExperience: 'none',
            congruence: 'congruent'
          },
          taskAssignment: {
            cohort: 'email-coding', // Mock cohort for dev
            taskOrder: 'email-first',
            primaryTask: 'email',
            secondaryTask: 'coding',
            stratum: 'coder',
            reasoning: 'Development mock assignment'
          },
          responses: []
        },
        startTime: Date.now()
      };
    }
    return value || null;
  };

  const [experimentData, setExperimentData] = useState<ExperimentData | null>(
    getMockData()
  );
  
  // FTT state management
  const [fttData, setFttData] = useState({
    emailClassifications: new Map<string, FTTClassification>(),
    deviceClassifications: new Map<string, FTTClassification>(),
    crossDomainMetrics: null as CrossDomainMetrics | null
  });

  // Update experimentData when value prop changes
  React.useEffect(() => {
    if (value) {
      setExperimentData(value);
    }
  }, [value]);

  const currentPhase = experimentData?.participant?.phase || 'pre-experiment';

  const updatePhase = useCallback((phase: ExperimentPhase) => {
    setExperimentData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        participant: {
          ...prev.participant,
          phase
        }
      };
      localStorage.setItem('experimentSession', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updateParticipant = useCallback((updates: Partial<Participant>) => {
    setExperimentData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        participant: {
          ...prev.participant,
          ...updates
        }
      };
      localStorage.setItem('experimentSession', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const saveResponse = useCallback((response: any) => {
    setExperimentData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        participant: {
          ...prev.participant,
          responses: [...(prev.participant.responses || []), response]
        }
      };
      localStorage.setItem('experimentSession', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const updateProgress = useCallback((progress: number) => {
    // This is a placeholder for progress tracking
    console.log('Progress updated:', progress);
  }, []);
  
  // FTT classification management
  const updateFTTClassification = useCallback((domain: 'email' | 'device' | 'info-sharing', id: string, classification: FTTClassification) => {
    setFttData(prev => {
      const newData = { ...prev };
      if (domain === 'email') {
        newData.emailClassifications = new Map(prev.emailClassifications).set(id, classification);
      } else {
        newData.deviceClassifications = new Map(prev.deviceClassifications).set(id, classification);
      }
      
      // Calculate cross-domain metrics if both domains have data
      if (newData.emailClassifications.size > 0 && newData.deviceClassifications.size > 0) {
        newData.crossDomainMetrics = calculateCrossDomainMetrics(
          Array.from(newData.emailClassifications.values()),
          Array.from(newData.deviceClassifications.values())
        );
      }
      
      return newData;
    });
  }, []);
  
  // Calculate cross-domain metrics
  const calculateCrossDomainMetrics = (emailClassifications: FTTClassification[], deviceClassifications: FTTClassification[]): CrossDomainMetrics => {
    // Calculate mode consistency
    const emailGistRatio = emailClassifications.filter(c => c.mode === 'gist').length / emailClassifications.length;
    const deviceGistRatio = deviceClassifications.filter(c => c.mode === 'gist').length / deviceClassifications.length;
    const processingModeConsistency = 1 - Math.abs(emailGistRatio - deviceGistRatio);
    
    // Calculate average confidence correlation
    const emailAvgConfidence = emailClassifications.reduce((sum, c) => sum + c.confidence, 0) / emailClassifications.length;
    const deviceAvgConfidence = deviceClassifications.reduce((sum, c) => sum + c.confidence, 0) / deviceClassifications.length;
    
    // Simple correlation for now - can be enhanced
    const responseTimeCorrelation = 0.5; // Placeholder - would need actual response times
    const decisionPatternSimilarity = processingModeConsistency; // Using mode consistency as proxy
    
    return {
      responseTimeCorrelation,
      decisionPatternSimilarity,
      processingModeConsistency,
      gistTransferScore: (emailGistRatio + deviceGistRatio) / 2 * processingModeConsistency
    };
  };

  const contextValue: ExperimentContextType = {
    experimentData,
    participant: experimentData?.participant || null,
    currentPhase,
    updatePhase,
    updateParticipant,
    saveResponse,
    updateProgress,
    fttData,
    updateFTTClassification
  };

  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
    </ExperimentContext.Provider>
  );
};